/**
 * Brand Manifesto — Decorative Icon Scatter
 *
 * Reads configuration from a [data-icon-count] container inside the section,
 * then distributes N icon images evenly across the free areas of the section,
 * avoiding text content and the mobile carousel.
 *
 * Key design decisions:
 *  - Icon size is read from the element's computed CSS width so the single
 *    source of truth lives in the stylesheet (35px mobile / 80px desktop).
 *  - On mobile the desktop `.section-brand-manifesto__blocks` is hidden, so
 *    exclusion zones are built from whichever elements are actually visible:
 *    desktop → `.section-brand-manifesto__blocks .section-brand-manifesto__block-inner`
 *    mobile  → `.section-brand-manifesto__carousel`
 *  - Placement uses a free-space-aware balanced distribution: the bounding
 *    box of the actual free cells (not the full section) is split into
 *    quadrants, ensuring icons spread evenly across whatever space is
 *    actually available — even when exclusions are large or asymmetric.
 *  - Each icon is randomly flipped horizontally for visual variety.
 *  - A staggered animation delay prevents synchronised floating movement.
 *  - Retries via requestAnimationFrame when section height is zero at first
 *    execution, capped at MAX_RETRIES to prevent infinite loops.
 *  - After window load (desktop only), optional re-placement (rAF + once
 *    after 2s) runs only if icons are vertically skewed or too close.
 *    Mobile skips that pipeline: the carousel blocks the bottom third, so
 *    “no icons in bottom third” is normal and was falsely triggering relayout.
 *  - ResizeObserver debounces checks: pairwise overlap (all viewports);
 *    any meaningful section width/height change triggers full re-place (all
 *    viewports) so mobile post-gate height growth is not skipped; desktop
 *    vertical skew still applies via the same dimChanged path.
 *  - Mobile: isVerticallyCollapsed catches a tall section whose icons still
 *    share one horizontal band (tryRelayout / post-gate without a resize tick).
 *  - Grid is inset from the section edges on desktop only (750px+); mobile
 *    uses the full section width/height for placement.
 *  - Post-load, resize, and delayed passes only re-place when the current
 *    layout is vertically skewed (e.g. none in lower thirds) or any two
 *    icons are closer than MIN_CENTER_GAP; otherwise positions are left alone.
 *  - Placement rejects random positions that violate MIN_CENTER_GAP until
 *    a cap of attempts, then uses cell-centre fallback.
 */

(function () {
  'use strict';

  /** Maximum rAF retries before giving up on a zero-height section. */
  var MAX_RETRIES = 10;

  /**
   * Minimum centre-to-centre distance between icons (px). Scales with size;
   * keeps icons from sitting on top of each other.
   */
  var MIN_CENTER_GAP_FACTOR = 1.22;

  /** Random placement attempts per icon before falling back to cell centre. */
  var MIN_GAP_PLACE_ATTEMPTS = 28;

  /** Padding (px) added around each exclusion zone. */
  var BLOCK_PADDING = 16;

  /** Minimum inner cell padding so icons never touch cell edges. */
  var CELL_INSET = 6;

  /** Breakpoint that matches the CSS mobile/desktop switch. */
  var DESKTOP_MIN_WIDTH = 750;

  /** Debounce for ResizeObserver → placeIcons (ms). */
  var RESIZE_DEBOUNCE_MS = 120;

  /** Delayed relayout after load (ms). */
  var POST_LOAD_DELAY_MS = 2000;

  /** ResizeObserver: treat width/height change as meaningful past this (px). */
  var RESIZE_DIM_THRESHOLD = 14;

  /**
   * Desktop-only placement inset from section edges (px): clamp(5% of min
   * side, min..max). On viewports below DESKTOP_MIN_WIDTH, inset is always 0.
   * If inner area would be too small on desktop, returns 0 inset.
   */
  var PLACEMENT_INSET_MIN = 16;
  var PLACEMENT_INSET_MAX = 56;
  var PLACEMENT_INSET_FRAC = 0.05;
  /** Minimum inner width/height (px) required to keep inset; else inset 0. */
  var PLACEMENT_MIN_INNER = 80;

  // ── Utilities ──────────────────────────────────────────────────────────────

  /** Matches CSS / buildExclusions mobile behaviour. */
  function isMobileViewport() {
    return window.innerWidth < DESKTOP_MIN_WIDTH;
  }

  /**
   * @param {function(): void} fn
   * @param {number} waitMs
   * @returns {function(): void}
   */
  function debounce(fn, waitMs) {
    var t = null;
    return function () {
      if (t !== null) clearTimeout(t);
      t = setTimeout(function () {
        t = null;
        fn();
      }, waitMs);
    };
  }

  /**
   * @param {number} pw
   * @param {number} ph
   * @returns {{ inset: number, innerW: number, innerH: number }}
   */
  function resolvePlacementInset(pw, ph) {
    if (window.innerWidth < DESKTOP_MIN_WIDTH) {
      return { inset: 0, innerW: pw, innerH: ph };
    }

    var minDim = Math.min(pw, ph);
    var inset = Math.round(minDim * PLACEMENT_INSET_FRAC);
    if (inset < PLACEMENT_INSET_MIN) inset = PLACEMENT_INSET_MIN;
    if (inset > PLACEMENT_INSET_MAX) inset = PLACEMENT_INSET_MAX;
    var innerW = pw - 2 * inset;
    var innerH = ph - 2 * inset;
    if (innerW < PLACEMENT_MIN_INNER || innerH < PLACEMENT_MIN_INNER) {
      return { inset: 0, innerW: pw, innerH: ph };
    }
    return { inset: inset, innerW: innerW, innerH: innerH };
  }

  /**
   * Returns true when rect A overlaps rect B.
   * @param {{ left: number, top: number, right: number, bottom: number }} a
   * @param {{ left: number, top: number, right: number, bottom: number }} b
   * @returns {boolean}
   */
  function rectsOverlap(a, b) {
    return a.left < b.right && a.right > b.left &&
           a.top  < b.bottom && a.bottom > b.top;
  }

  /**
   * Fisher-Yates in-place shuffle.
   * @template T
   * @param {T[]} arr
   * @returns {T[]}
   */
  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    return arr;
  }

  /**
   * Reads the rendered CSS width of a freshly-created (but not yet appended)
   * icon element by temporarily appending it to the container and measuring.
   * Falls back to the CSS breakpoint check if measurement fails.
   *
   * @param {HTMLElement} container  Icon container element.
   * @returns {number}
   */
  function resolveIconSize(container) {
    var probe = document.createElement('img');
    probe.className = 'section-brand-manifesto__icon';
    probe.style.visibility = 'hidden';
    probe.style.position   = 'absolute';
    container.appendChild(probe);
    var size = probe.offsetWidth || 0;
    container.removeChild(probe);
    if (size <= 0) {
      size = window.innerWidth >= DESKTOP_MIN_WIDTH ? 80 : 35;
    }
    return size;
  }

  /**
   * @param {HTMLElement} container
   * @returns {number}
   */
  function resolveIconSizeLive(container) {
    var img = container.querySelector('.section-brand-manifesto__icon');
    if (img && img.offsetWidth > 0) return img.offsetWidth;
    return resolveIconSize(container);
  }

  /**
   * Icon centres in section-local CSS pixels (matches left/top on icons layer).
   *
   * @param {HTMLElement} container
   * @param {HTMLElement} section
   * @returns {Array<{ cx: number, cy: number }>}
   */
  function getIconCenters(container, section) {
    var sectionRect = section.getBoundingClientRect();
    var imgs = container.querySelectorAll('.section-brand-manifesto__icon');
    var out = [];
    imgs.forEach(function (img) {
      var r = img.getBoundingClientRect();
      out.push({
        cx: (r.left + r.right) / 2 - sectionRect.left,
        cy: (r.top + r.bottom) / 2 - sectionRect.top,
      });
    });
    return out;
  }

  /**
   * True when icons are stacked toward the top / missing from lower bands.
   *
   * @param {Array<{ cx: number, cy: number }>} centers
   * @param {number} ph  Section height
   * @param {number} n   Expected icon count
   * @returns {boolean}
   */
  function isVerticallyUnbalanced(centers, ph, n) {
    if (isMobileViewport()) return false;

    if (n < 3 || ph <= 0) return false;

    var b1 = ph / 3;
    var b2 = (2 * ph) / 3;
    var c0 = 0;
    var c1 = 0;
    var c2 = 0;
    var minCy = Infinity;
    var maxCy = -Infinity;

    centers.forEach(function (p) {
      var cy = p.cy;
      if (cy < b1) c0++;
      else if (cy < b2) c1++;
      else c2++;
      if (cy < minCy) minCy = cy;
      if (cy > maxCy) maxCy = cy;
    });

    if (c2 === 0) return true;
    if (c1 === 0 && c2 === 0) return true;
    if (n >= 4 && maxCy - minCy < ph * 0.22) return true;

    return false;
  }

  /**
   * Mobile: icons share nearly the same Y while the section is tall (e.g. post-gate
   * scatter used a collapsed height). Desktop uses isVerticallyUnbalanced instead.
   *
   * @param {Array<{ cx: number, cy: number }>} centers
   * @param {number} ph
   * @param {number} n
   * @returns {boolean}
   */
  function isVerticallyCollapsed(centers, ph, n) {
    if (!isMobileViewport()) return false;
    if (n < 3 || ph < 200) return false;

    var minCy = Infinity;
    var maxCy = -Infinity;
    centers.forEach(function (p) {
      if (p.cy < minCy) minCy = p.cy;
      if (p.cy > maxCy) maxCy = p.cy;
    });

    return maxCy - minCy < ph * 0.2;
  }

  /**
   * @param {Array<{ cx: number, cy: number }>} centers
   * @param {number} iconSize
   * @returns {boolean}
   */
  function hasPairTooClose(centers, iconSize) {
    var minGap = iconSize * MIN_CENTER_GAP_FACTOR;
    var minSq = minGap * minGap;
    for (var i = 0; i < centers.length; i++) {
      for (var j = i + 1; j < centers.length; j++) {
        var dx = centers[i].cx - centers[j].cx;
        var dy = centers[i].cy - centers[j].cy;
        if (dx * dx + dy * dy < minSq) return true;
      }
    }
    return false;
  }

  /**
   * When true, a follow-up placeIcons is unnecessary (layout already OK).
   *
   * @param {HTMLElement} container
   * @param {HTMLElement} section
   * @returns {boolean}
   */
  function shouldSkipRelayout(container, section) {
    var count = Math.max(1, parseInt(container.dataset.iconCount, 10) || 0);
    if (!container.dataset.iconUrl) return true;

    var imgs = container.querySelectorAll('.section-brand-manifesto__icon');
    if (imgs.length !== count) return false;

    var ph = section.offsetHeight;
    var pw = section.offsetWidth;
    if (pw <= 0 || ph <= 0) return true;

    var centers = getIconCenters(container, section);
    if (centers.length !== count) return false;

    var iconSize = resolveIconSizeLive(container);

    if (isVerticallyUnbalanced(centers, ph, count)) return false;
    if (isVerticallyCollapsed(centers, ph, count)) return false;
    if (hasPairTooClose(centers, iconSize)) return false;

    return true;
  }

  /**
   * Persists section size after placement so ResizeObserver can detect real
   * viewport changes without re-scattering on every subpixel tick.
   *
   * @param {HTMLElement} container
   * @param {HTMLElement} section
   */
  function storeObservedDims(container, section) {
    container.dataset.manifestoIconsObsPw = String(section.offsetWidth);
    container.dataset.manifestoIconsObsPh = String(section.offsetHeight);
  }

  /**
   * Resize-only: fix overlaps always; when section width/height change enough,
   * re-place (all viewports). Mobile no longer relied on isVerticallyUnbalanced
   * (always false there), which missed post-gate height growth → row clustering.
   *
   * @param {HTMLElement} container
   * @param {HTMLElement} section
   */
  function tryRelayoutOnResize(container, section) {
    var count = Math.max(1, parseInt(container.dataset.iconCount, 10) || 0);
    if (!container.dataset.iconUrl) return;

    var imgs = container.querySelectorAll('.section-brand-manifesto__icon');
    if (imgs.length !== count) {
      placeIcons(container, section, 0);
      return;
    }

    var pw = section.offsetWidth;
    var ph = section.offsetHeight;
    if (pw <= 0 || ph <= 0) return;

    var centers = getIconCenters(container, section);
    if (centers.length !== count) {
      placeIcons(container, section, 0);
      return;
    }

    var iconSize = resolveIconSizeLive(container);

    if (hasPairTooClose(centers, iconSize)) {
      placeIcons(container, section, 0);
      return;
    }

    var lpw = parseFloat(container.dataset.manifestoIconsObsPw);
    var lph = parseFloat(container.dataset.manifestoIconsObsPh);
    var dimChanged =
      !isFinite(lpw) ||
      !isFinite(lph) ||
      Math.abs(pw - lpw) > RESIZE_DIM_THRESHOLD ||
      Math.abs(ph - lph) > RESIZE_DIM_THRESHOLD;

    if (dimChanged && ph >= PLACEMENT_MIN_INNER) {
      placeIcons(container, section, 0);
    }
  }

  // ── Balanced free-space distribution ───────────────────────────────────────

  /**
   * Selects `count` cells from `freeCells`, balanced across four quadrants
   * derived from the **bounding box of the free cells themselves** — not the
   * full section canvas. This means even when a large exclusion (e.g. the
   * carousel) removes the entire bottom of the section, the remaining free
   * space is still divided into equal quarters and icons fill each one evenly.
   *
   * Algorithm:
   *   1. Compute the axis-aligned bounding box of all free cells.
   *   2. Split that box into four equal quadrants at its midpoint.
   *   3. Assign each free cell to a quadrant by its centre.
   *   4. Take an equal share from each quadrant bucket (with one extra for
   *      any remainder), shuffling each bucket first for randomness.
   *   5. If a bucket is under-stocked, top up from remaining free cells in
   *      order of proximity to the deficient quadrant's centroid, so the
   *      fallback is spatially sensible rather than just arbitrary.
   *
   * @param {Array<{left:number,top:number,right:number,bottom:number}>} freeCells
   * @param {number} count
   * @returns {Array<{left:number,top:number,right:number,bottom:number}>}
   */
  function selectBalanced(freeCells, count) {
    if (freeCells.length === 0) return [];
    if (count >= freeCells.length) return shuffle(freeCells.slice());

    // Step 1 — bounding box of the free space.
    var minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    freeCells.forEach(function (c) {
      if (c.left   < minX) minX = c.left;
      if (c.top    < minY) minY = c.top;
      if (c.right  > maxX) maxX = c.right;
      if (c.bottom > maxY) maxY = c.bottom;
    });

    var midX = (minX + maxX) / 2;
    var midY = (minY + maxY) / 2;

    // Step 2-3 — assign each cell to a quadrant by its centre.
    function quadrant(cell) {
      var cx = (cell.left + cell.right)  / 2;
      var cy = (cell.top  + cell.bottom) / 2;
      if (cx < midX && cy < midY)  return 0; // top-left
      if (cx >= midX && cy < midY) return 1; // top-right
      if (cx < midX && cy >= midY) return 2; // bottom-left
      return 3;                               // bottom-right
    }

    var buckets = [[], [], [], []];
    freeCells.forEach(function (cell) { buckets[quadrant(cell)].push(cell); });
    buckets.forEach(shuffle);

    // Centroids of each quadrant (for proximity-based top-up).
    var qCentroids = [
      { x: (minX + midX) / 2, y: (minY + midY) / 2 },
      { x: (midX + maxX) / 2, y: (minY + midY) / 2 },
      { x: (minX + midX) / 2, y: (midY + maxY) / 2 },
      { x: (midX + maxX) / 2, y: (midY + maxY) / 2 },
    ];

    // Step 4 — take an equal share from each bucket.
    var perQuadrant = Math.floor(count / 4);
    var remainder   = count % 4;
    var selected    = [];
    var deficits    = []; // { qi, need } for under-stocked quadrants

    buckets.forEach(function (bucket, qi) {
      var take = perQuadrant + (qi < remainder ? 1 : 0);
      selected = selected.concat(bucket.slice(0, take));
      if (bucket.length < take) {
        deficits.push({ qi: qi, need: take - bucket.length });
      }
    });

    // Step 5 — proximity-aware top-up for under-stocked quadrants.
    if (selected.length < count) {
      var usedSet  = new Set(selected);
      var spare    = freeCells.filter(function (c) { return !usedSet.has(c); });

      deficits.forEach(function (deficit) {
        if (spare.length === 0) return;
        var centroid = qCentroids[deficit.qi];
        // Sort spare cells by distance to the deficient quadrant's centroid.
        spare.sort(function (a, b) {
          var acx = (a.left + a.right)  / 2, acy = (a.top + a.bottom) / 2;
          var bcx = (b.left + b.right)  / 2, bcy = (b.top + b.bottom) / 2;
          var da  = (acx - centroid.x) * (acx - centroid.x) + (acy - centroid.y) * (acy - centroid.y);
          var db  = (bcx - centroid.x) * (bcx - centroid.x) + (bcy - centroid.y) * (bcy - centroid.y);
          return da - db;
        });
        var take = Math.min(deficit.need, spare.length);
        for (var i = 0; i < take; i++) {
          selected.push(spare[i]);
        }
        spare = spare.slice(take);
      });
    }

    return selected.slice(0, count);
  }

  // ── Core placement ─────────────────────────────────────────────────────────

  /**
   * Builds the list of exclusion rectangles relative to the section origin.
   * On desktop this is the inner text content of each block.
   * On mobile the desktop blocks are `display:none`, so we exclude the
   * carousel element instead (icons must not overlap the swipeable cards).
   *
   * @param {HTMLElement} section
   * @param {number}      pw  Section pixel width
   * @param {number}      ph  Section pixel height
   * @returns {Array<{left:number,top:number,right:number,bottom:number}>}
   */
  function buildExclusions(section, pw, ph) {
    var sectionRect = section.getBoundingClientRect();
    var isMobile    = window.innerWidth < DESKTOP_MIN_WIDTH;

    var targets;
    if (isMobile) {
      // On mobile, exclude the entire carousel (track + nav buttons).
      targets = Array.from(section.querySelectorAll('.section-brand-manifesto__carousel'));
    } else {
      // On desktop, exclude only the inner text box, so icons can still
      // overlap the outer (invisible) block area.
      targets = Array.from(
        section.querySelectorAll(
          '.section-brand-manifesto__blocks .section-brand-manifesto__block-inner'
        )
      );
    }

    return targets
      .filter(function (el) { return el.offsetParent !== null; })
      .map(function (el) {
        var r = el.getBoundingClientRect();
        return {
          left:   Math.max(0,  r.left   - sectionRect.left - BLOCK_PADDING),
          top:    Math.max(0,  r.top    - sectionRect.top  - BLOCK_PADDING),
          right:  Math.min(pw, r.right  - sectionRect.left + BLOCK_PADDING),
          bottom: Math.min(ph, r.bottom - sectionRect.top  + BLOCK_PADDING),
        };
      });
  }

  /**
   * Places icon images inside the container element.
   *
   * @param {HTMLElement} container  Element carrying `data-icon-*` attributes.
   * @param {HTMLElement} section    The parent `<section>` element.
   * @param {number}      retries    Number of rAF retries attempted so far.
   */
  function placeIcons(container, section, retries) {
    if (retries === undefined) retries = 0;

    var pw = section.offsetWidth;
    var ph = section.offsetHeight;

    if ((pw <= 0 || ph <= 0) && retries < MAX_RETRIES) {
      requestAnimationFrame(function () { placeIcons(container, section, retries + 1); });
      return;
    }
    if (pw <= 0 || ph <= 0) return;

    var count    = Math.max(1, parseInt(container.dataset.iconCount, 10) || 0);
    var floating = container.dataset.iconFloat === 'true';
    var url      = container.dataset.iconUrl;

    if (!url) return;

    var iconSize   = resolveIconSize(container);
    var exclusions = buildExclusions(section, pw, ph);

    var placement = resolvePlacementInset(pw, ph);
    var inset     = placement.inset;
    var innerW    = placement.innerW;
    var innerH    = placement.innerH;

    // ── Grid ─────────────────────────────────────────────────────────────────
    // Build a grid ~4× denser than the icon count so there are always spare
    // candidates in every area after blocked cells are removed.
    // On tall/narrow mobile viewports the aspect ratio pushes cols down and
    // rows up, keeping cells roughly square. Inset applies on desktop only.
    var aspectRatio = innerW / innerH;
    var cols = Math.max(3, Math.ceil(Math.sqrt(count * 4 * aspectRatio)));
    var rows = Math.max(4, Math.ceil((count * 4) / cols));
    var cellW = innerW / cols;
    var cellH = innerH / rows;

    var freeCells = [];
    for (var row = 0; row < rows; row++) {
      for (var col = 0; col < cols; col++) {
        var cell = {
          left:   inset + col * cellW,
          top:    inset + row * cellH,
          right:  inset + (col + 1) * cellW,
          bottom: inset + (row + 1) * cellH,
        };
        var blocked = exclusions.some(function (ex) { return rectsOverlap(cell, ex); });
        if (!blocked) freeCells.push(cell);
      }
    }

    if (freeCells.length === 0) return;

    // ── Placement ────────────────────────────────────────────────────────────
    var selected = selectBalanced(freeCells, count);

    container.innerHTML = '';
    var fragment = document.createDocumentFragment();

    var minGap = iconSize * MIN_CENTER_GAP_FACTOR;
    var minGapSq = minGap * minGap;
    var placedCenters = [];

    selected.forEach(function (cell, idx) {
      var maxX = Math.max(0, (cell.right  - cell.left)  - iconSize - CELL_INSET * 2);
      var maxY = Math.max(0, (cell.bottom - cell.top)   - iconSize - CELL_INSET * 2);
      var baseLX = cell.left + CELL_INSET;
      var baseTY = cell.top  + CELL_INSET;

      var x = baseLX + (maxX > 0 ? maxX / 2 : 0);
      var y = baseTY + (maxY > 0 ? maxY / 2 : 0);
      var attempts = MIN_GAP_PLACE_ATTEMPTS;
      while (attempts-- > 0) {
        var tx = baseLX + (maxX > 0 ? Math.random() * maxX : 0);
        var ty = baseTY + (maxY > 0 ? Math.random() * maxY : 0);
        var cx = tx + iconSize / 2;
        var cy = ty + iconSize / 2;
        var ok = true;
        for (var pi = 0; pi < placedCenters.length; pi++) {
          var dx = cx - placedCenters[pi].cx;
          var dy = cy - placedCenters[pi].cy;
          if (dx * dx + dy * dy < minGapSq) {
            ok = false;
            break;
          }
        }
        if (ok) {
          x = tx;
          y = ty;
          break;
        }
      }

      placedCenters.push({ cx: x + iconSize / 2, cy: y + iconSize / 2 });

      var flip = Math.random() < 0.5 ? '1' : '-1';

      var img = new Image(iconSize, iconSize);
      img.src      = url;
      img.alt      = '';
      img.loading  = 'lazy';
      img.decoding = 'async';
      img.className = 'section-brand-manifesto__icon' +
        (floating ? ' section-brand-manifesto__icon--float' : '');

      img.style.left           = x + 'px';
      img.style.top            = y + 'px';
      img.style.animationDelay = (idx * 0.18) + 's';
      img.style.setProperty('--flip', flip);

      fragment.appendChild(img);
    });

    container.appendChild(fragment);
    storeObservedDims(container, section);
  }

  /**
   * Re-runs placement only when the current scatter is vertically skewed or
   * icons overlap the minimum gap (see shouldSkipRelayout).
   *
   * @param {HTMLElement} container
   * @param {HTMLElement} section
   */
  function tryRelayout(container, section) {
    if (shouldSkipRelayout(container, section)) return;
    placeIcons(container, section, 0);
  }

  /**
   * One-time: debounced ResizeObserver on the section + post-load rAF and
   * delayed tryRelayout so lazy media and late layout can fix bad first paint
   * without moving icons when the layout is already acceptable.
   *
   * @param {HTMLElement} container
   * @param {HTMLElement} section
   */
  function bindStableRelayout(container, section) {
    if (container.dataset.manifestoIconsRelayoutBound === 'true') return;
    container.dataset.manifestoIconsRelayoutBound = 'true';

    var debouncedTry = debounce(function () {
      tryRelayoutOnResize(container, section);
    }, RESIZE_DEBOUNCE_MS);

    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(debouncedTry);
      ro.observe(section);
    }

    function postLoadPasses() {
      if (isMobileViewport()) return;

      requestAnimationFrame(function () {
        tryRelayout(container, section);
      });
      setTimeout(function () {
        tryRelayout(container, section);
      }, POST_LOAD_DELAY_MS);
    }

    if (document.readyState === 'complete') {
      postLoadPasses();
    } else {
      window.addEventListener('load', postLoadPasses);
    }
  }

  // ── Initialisation ─────────────────────────────────────────────────────────

  /**
   * Initialises icon scattering for every brand-manifesto section on the page.
   * Safe to call multiple times (e.g. Shopify customiser re-render).
   */
  function init() {
    document.querySelectorAll('[data-icon-count]').forEach(function (container) {
      var section = container.closest('[data-section-id]');
      if (!section) return;
      placeIcons(container, section, 0);
      bindStableRelayout(container, section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { requestAnimationFrame(init); });
  } else {
    requestAnimationFrame(init);
  }

  /**
   * After a forced placeIcons, re-run tryRelayout on a staggered schedule so clustering /
   * vertical skew checks (same as post-load desktop passes) can fix bad scatters once layout
   * and carousel/text exclusions have settled — e.g. homepage hamster gate or display:none parents.
   * Unlike bindStableRelayout's postLoadPasses, this runs on mobile too (pairwise gap still matters).
   */
  function scheduleTryRelayoutAfterReveal(container, section) {
    function run() {
      tryRelayout(container, section);
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(run);
    });
    setTimeout(run, 120);
    setTimeout(run, 450);
    setTimeout(run, POST_LOAD_DELAY_MS);
  }

  /**
   * Re-run icon placement (e.g. after homepage hamster gate reveals content that was display:none).
   * Runs placeIcons then the same conditional relayout pipeline as tryRelayout (skew / too-close).
   * ResizeObserver bindings from init are unchanged.
   */
  window.refreshBrandManifestoIcons = function () {
    document.querySelectorAll('[data-icon-count]').forEach(function (container) {
      var section = container.closest('[data-section-id]');
      if (!section) return;
      placeIcons(container, section, 0);
      scheduleTryRelayoutAfterReveal(container, section);
    });
  };
})();
